import { useEffect, useState } from 'react';
import type { InventoryItem } from '@/modules/inventory/types';
import {
  createCheckIn,
  createCheckOut,
  createInventoryCount,
  createTransfer,
  listInventoryLocations,
  TRANSIT_LOCATION_SENTINEL,
} from '@/services/inventoryMovements';
import type { InventoryLocation } from '@/services/inventoryMovements/types';
import { useAuth } from '@/shared/hooks/useAuth';

type ItemMovementPanelProps = {
  item: InventoryItem;
  onUpdated?: () => void;
};

export function ItemMovementPanel({ item, onUpdated }: ItemMovementPanelProps) {
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('inventory.movements.write');
  const canLogistics = hasPermission('inventory.movements.logistics');
  const canAct = canWrite || canLogistics;

  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [toLocationId, setToLocationId] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const [countQty, setCountQty] = useState(String(item.stockQuantity));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void listInventoryLocations().then((locs) => {
      const active = locs.filter((l) => l.active);
      setLocations(active);
      if (active[0]) {
        setToLocationId((prev) => prev || active[0]!.id);
        setTransferTo((prev) => prev || active[1]?.id || active[0]!.id);
      }
    });
  }, []);

  const locationLabel = (id: string | null) => {
    if (!id || id === TRANSIT_LOCATION_SENTINEL) return 'En tránsito / fuera';
    return locations.find((l) => l.id === id)?.name ?? id.slice(0, 8);
  };

  async function run(action: () => Promise<unknown>) {
    if (!profile || !canAct) return;
    setBusy(true);
    setError(null);
    try {
      await action();
      onUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error en movimiento.');
    } finally {
      setBusy(false);
    }
  }

  if (!canAct) {
    return (
      <p className="auth-muted">
        Ubicación actual: {locationLabel(item.currentLocationId)}
      </p>
    );
  }

  return (
    <div className="inventory-panel">
      <h3>Movimientos físicos</h3>
      <p>
        Ubicación: <strong>{locationLabel(item.currentLocationId)}</strong>
        {item.lastMovementAt ? (
          <span className="auth-muted"> · último mov. {item.lastMovementAt}</span>
        ) : null}
      </p>
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="users-form-grid">
        <button
          type="button"
          className="auth-button auth-button--secondary"
          disabled={busy}
          onClick={() =>
            void run(() =>
              createCheckOut({ inventoryItemId: item.id }, profile!.uid),
            )
          }
        >
          Check-out
        </button>
        <label className="auth-label">
          Check-in a
          <select value={toLocationId} onChange={(e) => setToLocationId(e.target.value)}>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="auth-button auth-button--secondary"
          disabled={busy || !toLocationId}
          onClick={() =>
            void run(() =>
              createCheckIn(
                { inventoryItemId: item.id, toLocationId },
                profile!.uid,
              ),
            )
          }
        >
          Check-in
        </button>
      </div>
      {canWrite ? (
        <div className="users-form-grid">
          <label className="auth-label">
            Transferir a
            <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)}>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="auth-button auth-button--secondary"
            disabled={
              busy ||
              !transferTo ||
              !item.currentLocationId ||
              item.currentLocationId === TRANSIT_LOCATION_SENTINEL
            }
            onClick={() =>
              void run(() =>
                createTransfer(
                  {
                    inventoryItemId: item.id,
                    fromLocationId: item.currentLocationId!,
                    toLocationId: transferTo,
                  },
                  profile!.uid,
                ),
              )
            }
          >
            Transferir
          </button>
          <label className="auth-label">
            Conteo físico
            <input
              type="number"
              value={countQty}
              onChange={(e) => setCountQty(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="auth-button auth-button--secondary"
            disabled={busy || !toLocationId}
            onClick={() =>
              void run(() =>
                createInventoryCount(
                  {
                    inventoryItemId: item.id,
                    locationId: toLocationId,
                    countedQuantity: Number(countQty),
                  },
                  profile!.uid,
                ),
              )
            }
          >
            Registrar conteo
          </button>
        </div>
      ) : null}
    </div>
  );
}
